<script setup lang="ts">
import type { THEME } from '@pkg/config/misc/theme';

defineProps<{
  theme: THEME.LIGHT | THEME.DARK;
}>();

</script>

<template>
  <PartialSection :theme="theme" class="c-cta-section">
    <div class="c-cta-section__bg c-cta-section__bg--top" />
    <div class="c-cta-section__bg c-cta-section__bg--bottom" />
    <div class="c-cta-section__wrapper wrapper">
      <div class="c-cta-section__left-content">
        <slot />
      </div>
      <div class="c-cta-section__right-content">
        <slot name="right" />
      </div>
    </div>
  </PartialSection>
</template>

<style scoped>
.c-cta-section {
  /* Get the top background color */
  :root [data-theme="light"] + & {
    --cta-top-background-color: var(--theme-background-color-light);
  }
  :root [data-theme="light-alt"] + & {
    --cta-top-background-color: var(--theme-background-color-light-alt);
  }
  :root [data-theme="dark"] + & {
    --cta-top-background-color: var(--theme-background-color-dark);
  }
  :root [data-theme="dark-alt"] + & {
    --cta-top-background-color: var(--theme-background-color-dark-alt);
  }

  /* Get the bottom background color */
  &:has(+ [data-theme="light"]) {
    --cta-bottom-background-color: var(--theme-background-color-light);
  }
  &:has(+ [data-theme="light-alt"]) {
    --cta-bottom-background-color: var(--theme-background-color-light-alt);
  }
  &:has(+ [data-theme="dark"]) {
    --cta-bottom-background-color: var(--theme-background-color-dark);
  }
  &:has(+ [data-theme="dark-alt"]) {
    --cta-bottom-background-color: var(--theme-background-color-dark-alt);
  }

  padding: 0;

  @screen tablet-wide {
    padding-inline: theme('padding.40');
  }

  :root [data-theme="light"] + &:has(+ [data-theme="light"]),
  :root [data-theme="light-alt"] + &:has(+ [data-theme="light-alt"]),
  :root [data-theme="dark"] + &:has(+ [data-theme="dark"]),
  :root [data-theme="dark-alt"] + &:has(+ [data-theme="dark-alt"]) {
    @screen tablet-wide {

      & .c-cta-section__bg--bottom {
        background-color: var(--cta-top-background-color);
      }
    }
  }

  &__bg {
    position: absolute;
    inset-inline: 0;
    height: 50%;
    z-index: theme('zIndex.background');

    &--top {
      top: 0;
      background-color: var(--cta-top-background-color);
    }

    &--bottom {
      bottom: 0;
      background-color: var(--cta-bottom-background-color);
    }
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
    padding: theme('padding.56') theme('padding.20');
    background-color: var(--cta-front-background-color);
    z-index: theme('zIndex.page');

    @screen desk {
      flex-direction: row;
      align-items: center;
      justify-content: space-between;
      gap: 2rem;
      padding-inline: theme('padding.32');
    }
  }

  &__left-content {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 0.5rem;

    @screen desk {
      align-items: flex-start;
      text-align: left;

      max-width: 636px;
    }
  }

  &__right-content {
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;

    @screen desk {
      justify-content: flex-end;
    }
  }

  :deep(.c-base-title__header) {
    text-align: center;

    @screen desk {
      text-align: left;
    }
  }
}
</style>
